// useForceSimulation — owns the d3-force lifecycle for the org graph.
//
// Why a custom hook?
//   * The simulation must NOT restart on every React rerender. We only
//     restart when the node-id set or edge-pair set actually changes.
//   * Tick → state must be rAF-throttled so 60 ticks/sec don't trigger
//     60 React renders/sec; one render per animation frame is plenty.
//   * Cleanup must stop the simulation and cancel pending rAFs on unmount.
//
// Public contract:
//   useForceSimulation(nodes, edges, centerNodeId)
//     → { positions: Map<upn, {x,y}>, isSettled: boolean }
//
// `centerNodeId` is pinned to (0,0) via fx/fy before the simulation starts.
// If the id isn't in `nodes`, no pinning happens (and no console error).
//
// See specs/entra-org-chart/us3-force-and-zoom-hooks.md.
import { useEffect, useRef, useState } from 'react';
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  type Simulation,
  type SimulationNodeDatum,
  type SimulationLinkDatum,
} from 'd3-force';
import type { IOrgGraphNode, IOrgGraphEdge } from '@skystone/shared';
import { forceCluster, type ClusterNode } from './force-cluster';

export interface NodePosition {
  x: number;
  y: number;
}

interface ForceNode extends SimulationNodeDatum, ClusterNode {
  id: string;
  // ClusterNode.clusterId is what's actually used for the cluster force;
  // for the org graph the caller sets it to `managerUpn ?? id` so that
  // manager + direct reports form one visual pod.
}

type ForceLink = SimulationLinkDatum<ForceNode>;

export interface UseForceSimulationResult {
  positions: Map<string, NodePosition>;
  isSettled: boolean;
}

const EMPTY_RESULT: UseForceSimulationResult = {
  positions: new Map(),
  isSettled: true,
};

/**
 * Stable signature of the input set; recomputes only when the simulation
 * needs to restart. Includes each node's `managerUpn` because the cluster
 * force is built from manager membership at simulation-init time — a
 * manager reassignment must restart the simulation or clustering would
 * remain based on stale data. (Edges already encode manager→report pairs
 * directly so they'd usually catch this too; including managerUpn keeps
 * the invariant explicit even if a future change decouples edges.)
 *
 * `departmentId` is deliberately NOT part of the signature: departments are
 * render-derived overlays (node labels, filter grouping), not layout inputs,
 * so a department-only change must not shuffle the layout (PRODOPSAI-545).
 *
 * Note: `Array.prototype.sort` is given explicit `localeCompare` comparators
 * so behaviour is locale-stable (and to satisfy SonarCloud S2871).
 */
function buildSignature(nodes: IOrgGraphNode[], edges: IOrgGraphEdge[]): string {
  const nodeKeys = nodes
    .map((n) => `${n.id}@${n.managerUpn ?? ''}`)
    .sort((a, b) => a.localeCompare(b))
    .join('|');
  const edgePairs = edges
    .map((e) => `${e.source}>${e.target}`)
    .sort((a, b) => a.localeCompare(b))
    .join('|');
  return `${nodeKeys}::${edgePairs}`;
}

function snapshotPositions(simNodes: ForceNode[]): Map<string, NodePosition> {
  const out = new Map<string, NodePosition>();
  for (const node of simNodes) {
    const x = typeof node.x === 'number' && Number.isFinite(node.x) ? node.x : 0;
    const y = typeof node.y === 'number' && Number.isFinite(node.y) ? node.y : 0;
    out.set(node.id, { x, y });
  }
  return out;
}

export function useForceSimulation(
  nodes: IOrgGraphNode[],
  edges: IOrgGraphEdge[],
  centerNodeId: string | null,
): UseForceSimulationResult {
  const [result, setResult] = useState<UseForceSimulationResult>(EMPTY_RESULT);
  const signature = buildSignature(nodes, edges);
  const simulationRef = useRef<Simulation<ForceNode, ForceLink> | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef(false);

  useEffect(() => {
    if (nodes.length === 0) {
      setResult(EMPTY_RESULT);
      return;
    }

    // Precompute who has reports (= every edge source). Used both as the
    // orphan-bucket discriminator AND to detect sub-team leaders so we can
    // (a) cluster them with their OWN reports instead of their manager's
    // other reports, and (b) push them further away from their manager
    // via a longer link distance — without these two changes the cluster
    // force collapses a sub-leader's pod into its parent's pod, producing
    // the overlapping-hulls problem seen in bild9/bild10.
    const hasReports = new Set<string>();
    for (const edge of edges) hasReports.add(edge.source);

    const simNodes: ForceNode[] = nodes.map((n) => {
      let clusterId: string;
      if (n.managerUpn === null) {
        // Root: leads a team (cluster with own reports) or orphan.
        clusterId = hasReports.has(n.id) ? n.id : '__orphans__';
      } else if (hasReports.has(n.id)) {
        // Sub-team leader. Treat their OWN id as clusterId so the cluster
        // force pulls them toward THEIR reports' centroid, not their
        // manager's. Their tie to the parent is preserved by the link
        // force; this just stops the pod from being sucked back in.
        clusterId = n.id;
      } else {
        // Leaf member of a team: cluster with siblings under the manager.
        clusterId = n.managerUpn;
      }
      return { id: n.id, clusterId };
    });
    const simLinks: ForceLink[] = edges.map((e) => ({ source: e.source, target: e.target }));

    if (centerNodeId !== null) {
      const pinned = simNodes.find((n) => n.id === centerNodeId);
      if (pinned) {
        pinned.fx = 0;
        pinned.fy = 0;
      }
    }

    const sim: Simulation<ForceNode, ForceLink> = forceSimulation<ForceNode>(simNodes)
      .force(
        'link',
        forceLink<ForceNode, ForceLink>(simLinks)
          .id((d) => d.id)
          // Per-link distance: edges to sub-team leaders are 3× longer so
          // their pod ends up well clear of the parent's. Edges to leaf
          // reports stay short so leaves hug the manager.
          .distance((link) => {
            const targetId =
              typeof link.target === 'string'
                ? link.target
                : (link.target as ForceNode).id;
            return hasReports.has(targetId) ? 240 : 80;
          })
          .strength(0.6),
      )
      .force('charge', forceManyBody<ForceNode>().strength(-350))
      .force('center', forceCenter(0, 0))
      .force('collide', forceCollide<ForceNode>(20))
      .force('cluster', forceCluster(0.09));

    simulationRef.current = sim;

    const flush = () => {
      rafRef.current = null;
      pendingRef.current = false;
      setResult({
        positions: snapshotPositions(simNodes),
        isSettled: sim.alpha() < sim.alphaMin(),
      });
    };

    const scheduleFlush = () => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      rafRef.current = requestAnimationFrame(flush);
    };

    sim.on('tick', scheduleFlush);
    sim.on('end', () => {
      // Run a synchronous final flush so consumers see isSettled=true even
      // when the very last tick happens between animation frames.
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingRef.current = false;
      setResult({
        positions: snapshotPositions(simNodes),
        isSettled: true,
      });
    });

    return () => {
      sim.on('tick', null);
      sim.on('end', null);
      sim.stop();
      simulationRef.current = null;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingRef.current = false;
    };
    // We intentionally key on `signature` (id-set) + `centerNodeId`, not the
    // array references — see hook docstring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, centerNodeId]);

  return result;
}
